import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { COLLECTIONS } from '../lib/firebase/collections';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { CardCategory } from '../types/game';

interface AnalyticsData {
  activeLobbies: number;
  totalPlayersInActiveLobbies: number;
  gamesPlayed: number; // This would require tracking ended games
  categoryPopularity: { name: string, count: number }[];
}

const AnalyticsDashboard: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        // Get active lobbies and players
        const activeLobbiesQuery = query(collection(db, COLLECTIONS.LOBBIES), where('gameState', '!=', 'ended'));
        const activeLobbiesSnap = await getDocs(activeLobbiesQuery);
        const activeLobbiesCount = activeLobbiesSnap.size;
        const totalPlayers = activeLobbiesSnap.docs.reduce((acc, doc) => acc + (doc.data().players?.length || 0), 0);

        // Get ended games count
        const endedGamesQuery = query(collection(db, COLLECTIONS.LOBBIES), where('gameState', '==', 'ended'));
        const endedGamesSnap = await getDocs(endedGamesQuery);
        const gamesPlayedCount = endedGamesSnap.size;

        // Get category popularity
        const categoriesSnap = await getDocs(collection(db, COLLECTIONS.CARD_CATEGORIES));
        const categories = categoriesSnap.docs.map(d => ({ id: d.id, ...d.data() } as CardCategory));

        const popularity = categories.map(category => {
          const count = activeLobbiesSnap.docs.filter(lobby => lobby.data().selectedCategoryId === category.id).length;
          return { name: category.name, count };
        }).sort((a, b) => b.count - a.count);


        setData({
          activeLobbies: activeLobbiesCount,
          totalPlayersInActiveLobbies: totalPlayers,
          gamesPlayed: gamesPlayedCount,
          categoryPopularity: popularity,
        });
      } catch (error) {
        console.error("Fehler beim Laden der Analytics-Daten:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex justify-center items-center h-60">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg text-white">
      <h2 className="text-2xl font-bold mb-6">Analytics Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-700 p-4 rounded-lg text-center">
          <h3 className="text-lg font-semibold text-primary-300">Aktive Lobbys</h3>
          <p className="text-4xl font-bold">{data.activeLobbies}</p>
        </div>
        <div className="bg-gray-700 p-4 rounded-lg text-center">
          <h3 className="text-lg font-semibold text-primary-300">Spieler Online</h3>
          <p className="text-4xl font-bold">{data.totalPlayersInActiveLobbies}</p>
        </div>
        <div className="bg-gray-700 p-4 rounded-lg text-center">
          <h3 className="text-lg font-semibold text-primary-300">Spiele Gespielt</h3>
          <p className="text-4xl font-bold">{data.gamesPlayed}</p>
          <p className="text-xs text-gray-400">(Feature in Entwicklung)</p>
        </div>
      </div>

      <div>
        <h3 className="text-xl font-bold mb-4">Kategorien-Popularität</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.categoryPopularity} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
            <XAxis dataKey="name" stroke="#A0AEC0" />
            <YAxis stroke="#A0AEC0" />
            <Tooltip contentStyle={{ backgroundColor: '#2D3748', border: 'none' }} />
            <Legend />
            <Bar dataKey="count" fill="#4299E1" name="Anzahl Spiele" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AnalyticsDashboard; 